// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import RoundedButtonWithIcon from '../../../../components/RoundedButtonWithIcon';
import SpinePlayer from '../../../../components/SpinePlayer';
import useDelayedPropVariation from '../../../../hooks/useDelayedPropVariation';
import { __ } from '../../../../i18n/l10n';
import colors from '../../../../styled-components/palette';
import { GRLocker, GRRoundWonRibbonImgPath } from '../../assets/img';
import { GRVideoIconSvg } from '../../assets/svg';
import { GRRoundContext } from '../../contexts/GRRoundContext';
import useGRHomeContext from '../../hooks/useGRHomeContext';
import { GRSkinId, GRSkinIds } from '../../types/global';
import { GRRounds, type GRRound as GRRoundType } from '../../types/round';
import GRChipDetailsContainer from '../GRChipDetails/GRChipDetailsContainer';
import GRChipListContainer from '../GRChipList/GRChipListContainer';
import GRProgressBarContainer from '../GRProgressBar/GRProgressBarContainer';
import GRRoundWonTagContainer from '../GRRoundWonTag/GRRoundWonTagContainer';
import GRRulesButtonContainer from '../GRRulesButton/GRRulesButtonContainer';

type BackgroundsProps = {
  $backgroundPath: string;
};

const Background = styled.div<BackgroundsProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  z-index: -1;

  ${({ $backgroundPath }) =>
    $backgroundPath
      ? css`
          background-image: url(${$backgroundPath});
          background-size: cover;
          background-repeat: no-repeat;
          background-position: ${Target.__MOBILE__ ? 'center' : 'center 40%'};
        `
      : undefined}
`;

const appearAnim = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Locker = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  background-image: url(${GRLocker});
  background-repeat: no-repeat;
  background-size: contain;
  width: 7em;
  height: 7em;

  &.locker-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }

  &.locker-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }
`;

type GRChipDetailsWrapperProps = {
  $show: boolean;
};

const GRChipDetailsWrapper = styled.div<GRChipDetailsWrapperProps>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(0deg, #000000ff, #000000aa, #00000000);
  opacity: 0;
  padding: 1em;
  z-index: 1;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  transition: none 300ms ease-in-out;
  transition-property: transform, opacity;
  padding-bottom: 14%;

  ${({ $show }) => {
    if ($show) {
      return css`
        transform: translateY(-100%);
        opacity: 1;
      `;
    }
  }};
`;

const animGrayscale = keyframes`
  from {
    filter: grayscale(1);
    pointer-events: none;
  }

  to {
    filter: grayscale(0);
    pointer-events: auto;
  }
`;

type WrapperProps = {
  $wasPreviouslyLocked: boolean;
  $isLocked: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  padding: 1em;
  transform: translateZ(0);
  overflow: hidden;
  height: 100%;
  max-height: 100%;

  & > *:not(${Locker}):not(${GRChipDetailsWrapper}) {
    transition: filter 1000ms ease-in-out;
  }

  ${({ $wasPreviouslyLocked, $isLocked }) =>
    $isLocked
      ? css`
          & > *:not(${Locker}):not(${GRChipDetailsWrapper}) {
            filter: grayscale(1);
          }
          pointer-events: none;
        `
      : css`
          & > *:not(${Locker}):not(${GRChipDetailsWrapper}) {
            ${$wasPreviouslyLocked
              ? css`
                  animation: ${animGrayscale} 1000ms ease-in-out forwards;
                `
              : undefined}
          }
        `}
`;

const RightColumn = styled.div`
  position: absolute;
  right: 0;
  top: 4em;
  bottom: 0;
  width: 20%;
  height: 99%;
  z-index: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledRulesBtn = styled(GRRulesButtonContainer)`
  position: absolute;
  right: 0.6em;
  top: 0.6em;

  ${Target.__MOBILE__
    ? css`
        top: 0.6em;
      `
    : css`
        top: 1.4em;
      `}
`;

const StyledGRChipDetailsContainer = styled(GRChipDetailsContainer)`
  &.chip-details-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }

  &.chip-details-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }
`;

const spinePositioningByRoundId = {
  [GRSkinIds.GIFT_RACE]: {
    [GRRounds.SHOE]: css`
      left: -2.5em;
      top: 7.5em;
      transform: scale(2);
    `,
    [GRRounds.BALLOON]: css`
      left: 0em;
      top: 55%;
      transform: translateY(-50%) scale(2.5);
    `,
    [GRRounds.SHIRT]: css`
      left: 0em;
      top: 50%;
      transform: translateY(-50%) scale(1.25);
    `,
  },
  [GRSkinIds.HUNTING_ALL_STARS]: {
    [GRRounds.SHOE]: css`
      left: 0;
      top: 52%;
      transform: translateY(-50%) scale(2);
    `,
    [GRRounds.BALLOON]: css`
      left: 0em;
      top: 52%;
      transform: translateY(-50%) scale(2);
    `,
    [GRRounds.SHIRT]: css`
      left: 1em;
      top: 45%;
      transform: translateY(-50%) scale(2.3);
    `,
  },
};

const StyledSpinePlayerWrapper = styled.div`
  height: 100%;
  position: relative;
`;

type StyledSpinePlayerProps = {
  $roundId: GRRoundType;
  $skinId: GRSkinId;
};

const StyledSpinePlayer = styled(SpinePlayer)<StyledSpinePlayerProps>`
  position: absolute;

  ${({ $roundId, $skinId }) => spinePositioningByRoundId[$skinId][$roundId]}
`;

const StyledGRProgressBarContainer = styled(GRProgressBarContainer)`
  position: absolute;
  bottom: 3em;
  left: 50%;
  transform: translateX(-50%);
  width: 17em;
`;

const ribbonImgRatio = 1.41136363636;
const ribbonWidth = 50; // %
const ribbonWidthFallback = 20; // em

const WonRibbon = styled.div`
  position: absolute;
  left: 0;
  top: -1.2em;
  width: min(${ribbonWidth}%, ${ribbonWidthFallback}em);
  padding-bottom: min(${ribbonImgRatio * ribbonWidth}%, ${ribbonImgRatio * ribbonWidthFallback}em);
  background-image: url(${GRRoundWonRibbonImgPath});
  background-size: contain;
  background-repeat: no-repeat;

  &:after {
    content: '${__('!noun:Won')}';
    position: absolute;
    color: white;
    bottom: 19%;
    left: 8%;
    transform: rotate(-52deg);
    transform-origin: left center;
    font-size: 1.9em;
    font-weight: bold;
    width: 5em;
    text-align: center;
  }
`;

const StyledGRRoundWonTagContainer = styled(GRRoundWonTagContainer)`
  position: absolute;
  bottom: 3em;
  left: 55%;
  transform: translateX(-50%);
`;

const Alert = styled.div`
  position: absolute;
  top: 99%;
  left: 50%;
  width: 90%;
  transform: translate(-50%, -90%);
  pointer-events: none;
  opacity: 0.8;
  background: ${colors.red200};
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.3em 0.5em;
  border-radius: 0.3em;
  font-weight: 600;
  font-size: 0.9em;
`;

const VideoTutorialButtonWrapper = styled.div`
  position: absolute;
  right: 0.5em;
  top: 4em;
  font-weight: bold;
  // Make the button above the chip list
  z-index: 2;
`;

const VideoTutorialButton = styled(RoundedButtonWithIcon)`
  transition: transform 150ms ease-in-out;

  background-color: white;
  color: black;

  width: 100%;

  > div {
    margin-bottom: -0.3em;
  }
`;

const tutoVideoButtonIconStyle = css`
  width: 1.5em;
  height: 1.5em;
  padding-top: 0.1em;
`;

type GRRoundProps = {
  className?: string;
  roundId: GRRoundType;
  backgroundPath?: string;
  animationId?: string;
  children?: React.ReactNode;
  isLocked: boolean;
  isWon: boolean;
  skinId: GRSkinId;
  showAlert?: boolean;
  showVideoTutorialButton?: boolean;
  onVideoTutorialClick?: () => void;
};

const GRRound = ({
  className,
  roundId,
  backgroundPath,
  animationId,
  isLocked,
  isWon,
  skinId,
  children,
  showAlert = false,
  showVideoTutorialButton = false,
  onVideoTutorialClick,
}: GRRoundProps) => {
  const [delayedIsLocked, prevDelayedIsLocked] = useDelayedPropVariation(isLocked);
  const [lastSelectedChipIndex, setLastSelectedChipIndex] = useState<null | number>(null);
  const [chipDetailsPanelIsOpened, setChipDetailsPanelIsOpened] = useState(false);
  const { fullScreenNotifShown } = useGRHomeContext();

  const onChipClick = useCallback((index: number) => {
    setLastSelectedChipIndex(index);
    setChipDetailsPanelIsOpened(true);
  }, []);

  const contextState = useMemo(
    () => ({
      roundId,
      isLocked,
      isWon,
      onChipClick,
      lastSelectedChipIndex,
      chipDetailsPanelIsOpened,
    }),
    [chipDetailsPanelIsOpened, lastSelectedChipIndex, isWon, onChipClick, roundId, isLocked],
  );

  const onGlobalClick = useCallback(() => {
    setChipDetailsPanelIsOpened(false);
  }, []);

  useEffect(() => {
    if (fullScreenNotifShown) {
      onGlobalClick();
    }
  }, [fullScreenNotifShown, onGlobalClick]);

  return (
    <GRRoundContext.Provider value={contextState}>
      <Wrapper
        className={className}
        onClick={onGlobalClick}
        $wasPreviouslyLocked={prevDelayedIsLocked}
        $isLocked={delayedIsLocked}
      >
        <Background $backgroundPath={backgroundPath} />
        {isWon ? <WonRibbon /> : null}
        {!delayedIsLocked ? (
          <StyledSpinePlayerWrapper>
            <StyledSpinePlayer
              $roundId={roundId}
              $skinId={skinId}
              animationId={animationId}
              width={1500}
              height={1500}
              hideMuteButton
            />
          </StyledSpinePlayerWrapper>
        ) : null}
        {!isWon ? <StyledRulesBtn /> : null}
        {showVideoTutorialButton ? (
          <VideoTutorialButtonWrapper>
            <VideoTutorialButton
              label={__('Tutorial')}
              onClick={onVideoTutorialClick}
              SVGIcon={GRVideoIconSvg}
              SVGIconStyle={tutoVideoButtonIconStyle}
              iconLeft
            />
          </VideoTutorialButtonWrapper>
        ) : null}
        <RightColumn>
          <GRChipListContainer />
        </RightColumn>
        <StyledGRProgressBarContainer />
        <StyledGRRoundWonTagContainer />
        <GRChipDetailsWrapper $show={chipDetailsPanelIsOpened}>
          <CSSTransition
            classNames={'chip-details-transition'}
            in={chipDetailsPanelIsOpened}
            timeout={300}
            unmountOnExit
            mountOnEnter
          >
            <StyledGRChipDetailsContainer />
          </CSSTransition>
        </GRChipDetailsWrapper>
        <CSSTransition classNames={'locker-transition'} in={delayedIsLocked} timeout={1000} unmountOnExit mountOnEnter>
          <Locker />
        </CSSTransition>
        {children}
        {showAlert ? <Alert>{__('Your winning and ongoing bets already exceed the objective')}</Alert> : null}
      </Wrapper>
    </GRRoundContext.Provider>
  );
};

export default GRRound;
